import React, { useEffect, FC, useState, useCallback } from "react";
import { Modal, ModalBody, Form, Row, Alert, Col, Button } from "reactstrap";
import { FormProvider, useFieldArray, useForm } from "react-hook-form";
import FormInput from "../form-components/InputComponent";
import { Icon, TooltipComponent } from "../../components/Component";
import FormSelect from "../form-components/SelectComponent";
import FormDatePicker from "../form-components/DatePicker";
import FormTextArea from "../form-components/TextAreaComponent";
import { Popover } from "antd";
import deliveryAddressApis from "../../api/master/deliveryAddress";
import { nanoid } from "nanoid";
import supplierApis from "../../api/master/supplier";
import taxMasterApis from "../../api/master/taxmaster";
interface SelectOptions {
  label: string;
  value: any;
  taxRate?: number;
  taxName?: string;
}

type Cess = {
  cessName: string;
  cessType?: string;
  cessPercentage?: string;
  cessAmount?: string;
};
interface TaxConfiguration {
  taxType: string;
  gstType?: string; //IGST or CGST+SGST
  cess?: Cess[];
}
const MaterialModal: FC<any> = ({
  modal,
  closeModal,
  append,
  update,
  formData,
  taxConfiguration,
  needByDateType,
  deliveryAddressType,
  priceSlab,
  contractType,
  materialCategoryOptions,
  materialsList,
  siteId,
}) => {
  const [sm, setSm] = useState(false);
  const [screenSize, setScreenSize] = useState(0);
  const [mobileView, setMobileView] = useState(false);

  const [uomOptions, setUomOptions] = useState<SelectOptions[]>([]);
  const [materialData, setMaterialData] = useState<any>();
  const [materialOptions, setMaterialOptions] = useState<SelectOptions[]>([]);
  const [selectedMaterials, setSelectedMaterials] = useState<string[]>([]);

  const [deliveryAddressList, setDeliveryAddressList] = useState<any>([]);
  const [deliveryAddressOptions, setDeliveryAddressOptions] = useState<
    SelectOptions[]
  >([]);
  const [dmsFile, setDmsFile] = useState<any>(null);
  const [taxOptions, setTaxOptions] = useState<SelectOptions[]>([]);

  const methods = useForm({
    mode: "onChange",
  });
  const {
    handleSubmit,
    watch,
    formState: { errors },
    setValue,
  } = methods;

  const [alertInfo, setAlertInfo] = useState<{
    type: string;
    message: string;
    errors: string[];
  }>({
    type: "", // 'success' or 'error'
    message: "",
    errors: [],
  });

  const [priceSlabErrors, setPriceSlabErrors] = useState<{
    [key: number]: {
      minquantity?: string;
      maxquantity?: string;
    };
  }>({});

  const { control } = methods;

  const {
    fields: priceSlabFields,
    append: appendPriceSlab,
    remove: removePriceSlab,
    replace,
  } = useFieldArray({
    control,
    name: "priceSlabArray",
  });

  const files = watch("attachment");
  const priceSlabArrayWatch = watch("priceSlabArray");

  // Validation function for price slabs
  const validatePriceSlabs = (priceSlabArray: any[]) => {
    if (!priceSlabArray || priceSlabArray.length === 0) {
      return { isValid: true, errors: [], fieldErrors: {} };
    }

    const errors: string[] = [];
    const fieldErrors: {
      [key: number]: { minquantity?: string; maxquantity?: string };
    } = {};

    const slabs = priceSlabArray.map((slab, index) => ({
      index,
      min: Number(slab.minquantity),
      max: Number(slab.maxquantity),
      hasMin:
        slab.minquantity !== "" &&
        slab.minquantity !== null &&
        slab.minquantity !== undefined,
      hasMax:
        slab.maxquantity !== "" &&
        slab.maxquantity !== null &&
        slab.maxquantity !== undefined,
    }));

    // Check if min equals max for any row
    // Only validate if both values are valid numbers (not NaN)
    for (const slab of slabs) {
      if (slab.hasMin && slab.hasMax) {
        // Only validate if both are valid numbers
        if (!isNaN(slab.min) && !isNaN(slab.max)) {
          if (slab.min === slab.max) {
            const errorMsg =
              "Minimum quantity cannot be equal to maximum quantity";
            errors.push(`Row ${slab.index + 1}: ${errorMsg}`);
            if (!fieldErrors[slab.index]) fieldErrors[slab.index] = {};
            fieldErrors[slab.index].minquantity = errorMsg;
            fieldErrors[slab.index].maxquantity = errorMsg;
          }
          if (slab.min > slab.max) {
            const errorMsg =
              "Minimum quantity cannot be greater than maximum quantity";
            errors.push(`Row ${slab.index + 1}: ${errorMsg}`);
            if (!fieldErrors[slab.index]) fieldErrors[slab.index] = {};
            fieldErrors[slab.index].minquantity = errorMsg;
            fieldErrors[slab.index].maxquantity = errorMsg;
          }
        }
      }
    }

    // Only validate overlaps and gaps if all values are valid numbers
    const validSlabs = slabs.filter(
      (slab) =>
        slab.hasMin &&
        slab.hasMax &&
        !isNaN(slab.min) &&
        !isNaN(slab.max) &&
        slab.min < slab.max
    );

    if (validSlabs.length > 0) {
      // Sort slabs by min quantity
      const sortedSlabs = [...validSlabs].sort((a, b) => a.min - b.min);

      // Check for overlaps and gaps
      for (let i = 0; i < sortedSlabs.length; i++) {
        const current = sortedSlabs[i];

        // Check for overlaps with other slabs
        for (let j = i + 1; j < sortedSlabs.length; j++) {
          const other = sortedSlabs[j];
          // Check if ranges overlap
          if (
            (current.min <= other.max && current.max >= other.min) ||
            (other.min <= current.max && other.max >= current.min)
          ) {
            const errorMsg = `Overlapping with Row ${other.index + 1}`;
            errors.push(
              `Row ${current.index + 1} (${current.min}-${
                current.max
              }) and Row ${other.index + 1} (${other.min}-${
                other.max
              }) have overlapping ranges`
            );
            if (!fieldErrors[current.index]) fieldErrors[current.index] = {};
            if (!fieldErrors[other.index]) fieldErrors[other.index] = {};
            fieldErrors[current.index].maxquantity = errorMsg;
            fieldErrors[other.index].minquantity = `Overlapping with Row ${
              current.index + 1
            }`;
          }
        }

        // Check for gaps or overlaps with previous slab (only if not the first slab)
        if (i > 0) {
          const previous = sortedSlabs[i - 1];
          // Next min should be exactly previous max + 1 (continuous)
          if (current.min <= previous.max) {
            // Overlapping
            const errorMsg = `Overlapping with Row ${previous.index + 1}`;
            errors.push(
              `Row ${previous.index + 1} (${previous.min}-${
                previous.max
              }) and Row ${current.index + 1} (${current.min}-${
                current.max
              }) have overlapping ranges`
            );
            if (!fieldErrors[previous.index]) fieldErrors[previous.index] = {};
            if (!fieldErrors[current.index]) fieldErrors[current.index] = {};
            fieldErrors[previous.index].maxquantity = `Overlapping with Row ${
              current.index + 1
            }`;
            fieldErrors[current.index].minquantity = errorMsg;
          } else if (current.min > previous.max + 1) {
            // Gap
            const errorMsg = `Gap detected. Should be ${previous.max + 1}`;
            errors.push(
              `Row ${previous.index + 1} (${previous.min}-${
                previous.max
              }) and Row ${current.index + 1} (${current.min}-${
                current.max
              }) have a gap. Ranges should be continuous (next minimum should be previous maximum + 1)`
            );
            if (!fieldErrors[current.index]) fieldErrors[current.index] = {};
            fieldErrors[current.index].minquantity = errorMsg;
          }
        }
      }
    }

    return {
      isValid: errors.length === 0,
      errors,
      fieldErrors,
    };
  };

  useEffect(() => {
    if (priceSlab === "true" && priceSlabFields.length === 0) {
      appendPriceSlab({ minquantity: "", maxquantity: "", unitPrice: "" });
    }
  }, [priceSlab]);

  // Function to trigger validation immediately
  const triggerValidation = useCallback(() => {
    if (priceSlab === "true") {
      // Use a small timeout to ensure form state is updated
      setTimeout(() => {
        const currentValues = watch("priceSlabArray");
        if (currentValues) {
          const validation = validatePriceSlabs(currentValues);
          setPriceSlabErrors(validation.fieldErrors || {});
          // Only show field-level errors, not alert at top
        }
      }, 10);
    }
  }, [priceSlab, watch]);

  // Validate price slabs when they change
  useEffect(() => {
    if (priceSlab === "true" && priceSlabArrayWatch) {
      triggerValidation();
    } else {
      setPriceSlabErrors({});
    }
  }, [priceSlabArrayWatch, priceSlab]);

  useEffect(() => {
    if (files && files[0]?.size && files[0].size > 2097152) {
      setValue("attachment", null);
      setAlertInfo({
        type: "error",
        message: "File size should be less than 2 MB",
        errors: [],
      });
    }
  }, [files]);

  const deliveryAddressId = watch("deliveryAddressId");
  const siteIdWatch = watch("siteId");

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setAlertInfo({
        type: "",
        message: "",
        errors: [],
      });
    }, 2500);
    return () => clearTimeout(timeoutId);
  }, [alertInfo]);

  const fetchDeliveryAddress = async () => {
    try {
      if (siteId) {
        const { data, status, message } =
          await deliveryAddressApis.fetchAddressBySite(siteId);
        if (status) {
          let deliveryAddressOptions = data.map((item: any) => ({
            label: item.deliveryLocationName,
            value: item.id.toString(),
          }));
          setDeliveryAddressOptions(deliveryAddressOptions);
          setDeliveryAddressList(data);
        } else {
          console.log(message);
        }
      }
    } catch (error) {
      console.log("Error fetching delivery addresses:", error);
    }
  };

  useEffect(() => {
    fetchDeliveryAddress();
  }, [siteId]);

  const fetchTaxOptions = async () => {
    try {
      const { data, status } = await taxMasterApis.fetchTaxDropDown(
        "INDIRECT,CESS"
      );
      if (status && data) {
        // Transform API response to dropdown format
        const options = data.map((tax: any) => {
          const rate = Number(tax.taxRate);
          // Format label: show tax name and percentage in braces (e.g., "CGST (18%)" or "IGST (2.50%)")
          const rateLabel = rate % 1 === 0 ? `${rate}%` : `${rate.toFixed(2)}%`;
          const label = `${tax.taxName} (${rateLabel})`;
          return {
            label,
            value: tax.taxId.toString(),
            taxRate: rate,
            taxName: tax.taxName,
          };
        });
        setTaxOptions(options);
      }
    } catch (error) {
      console.log("Error fetching tax options:", error);
    }
  };

  useEffect(() => {
    fetchTaxOptions();
  }, []);

  // Filter tax options based on tax configuration
  const getFilteredTaxOptions = useCallback(() => {
    if (!taxConfiguration?.gstType) return taxOptions;

    if (taxConfiguration.gstType === "IGST") {
      // For IGST, return IGST options
      return taxOptions.filter(
        (option: any) => option.taxName === "IGST" || option.taxName === "GST"
      );
    } else if (taxConfiguration.gstType === "CGST+SGST") {
      // For CGST+SGST, return CGST or SGST options (they should have same rates)
      return taxOptions.filter(
        (option: any) => option.taxName === "CGST" || option.taxName === "SGST"
      );
    }
    return taxOptions;
  }, [taxOptions, taxConfiguration?.gstType]);

  const fetchUom = async (materialId: any, materialData: any, uomId: any) => {
    try {
      const materialUom = materialData?.find(
        (item: any) => item.materialId == materialId
      );
      if (!materialUom) return;
      const _uomOptions = [
        {
          value: materialUom?.primaryUomId,
          label: materialUom?.PrimaryUomName,
        },
      ];
      if (
        materialUom?.secondaryUomId &&
        materialUom?.primaryUomId != materialUom?.secondaryUomId &&
        materialUom?.PrimaryUomName != materialUom?.secondaryUomName
      ) {
        _uomOptions.push({
          value: materialUom?.secondaryUomId,
          label: materialUom?.secondaryUomName,
        });
      }
      setValue("uomId", uomId ? uomId : materialUom?.primaryUomId);
      setUomOptions(_uomOptions);
    } catch (error) {
      console.log(error);
    }
  };

  const fetchMaterials = async (categoryId: any, materialId: any) => {
    try {
      const { data, status } = await supplierApis.fetchCategoryProducts(
        categoryId
      );
      if (status) {
        const materialOption = data.map((item: any) => ({
          label: item?.materialName,
          value: item?.materialId?.toString(),
        }));
        if (
          (formData.action === "add" || formData.index === null) &&
          needByDateType == "Order Level" &&
          deliveryAddressType == "Order Level"
        ) {
          if (Array.isArray(materialOption)) {
            const filteredOptions = materialOption.filter(
              (option) =>
                !materialsList || !materialsList.includes(option.value)
            );
            setMaterialOptions(filteredOptions);
          }
        } else {
          setMaterialOptions(materialOption);
        }
        setMaterialData(data);
        setValue("materialName", materialId);
        return data;
      }
    } catch (error) {
      console.log(error);
    }
  };

  const viewChange = () => {
    setScreenSize(window.innerWidth);
    if (window.innerWidth < 990) {
      setMobileView(true);
    } else {
      setMobileView(false);
      setSm(false);
    }
  };

  useEffect(() => {
    viewChange();
    window.addEventListener("load", viewChange);
    window.addEventListener("resize", viewChange);
    const headerClick = () => setSm(false);
    document
      .getElementsByClassName("nk-header")[0]
      ?.addEventListener("click", headerClick);

    return () => {
      window.removeEventListener("resize", viewChange);
      window.removeEventListener("load", viewChange);
      document
        .getElementsByClassName("nk-header")[0]
        ?.removeEventListener("click", headerClick);
    };
  }, []);
  useEffect(() => {
    if (modal && formData.item) {
      const {
        materialCategory,
        materialName,
        hsnCode,
        quantity,
        uomId,
        unitPrice,
        priceSlabArray,
        cgst,
        sgst,
        igst,
        deliveryAddressId,
        description,
        needByDate,
        file,
        dms,
      } = formData.item;
      console.log(formData.item);
      setValue("materialCategory", materialCategory);
      (async () => {
        const _materialData = await fetchMaterials(
          materialCategory,
          materialName
        );
        fetchUom(materialName, _materialData, uomId);
      })();
      setValue("hsnCode", hsnCode);
      setValue("quantity", quantity);
      setValue("unitPrice", Number(unitPrice));
      setValue("cgst", cgst);
      setValue("sgst", sgst);
      setValue("igst", igst);
      setValue("description", description);
      setValue("attachment", file);
      if (formData.index != null) {
        setValue("deliveryAddressId", deliveryAddressId);
        setValue("needByDate", needByDate);
      }
      if (dms?.length > 0) {
        setDmsFile(dms[0]);
      }
      if (priceSlabArray?.length > 0) {
        setValue("priceSlab", "true");
        replace(priceSlabArray); // replaces the entire field array
      }
    }
  }, [modal]);

  const submitForm = (data: any) => {
    console.log("data", data);
    const {
      materialCategory,
      materialName,
      hsnCode,
      quantity,
      unitPrice,
      igst,
      cgst,
      description,
      uomId,
      needByDate,
      attachment,
      deliveryAddressId,
      priceSlabArray,
    } = data;

    // Validate price slabs if price slab is enabled (skip for Open contract type)
    if (priceSlab === "true" && contractType !== "Open" && priceSlabArray && priceSlabArray.length > 0) {
      const validation = validatePriceSlabs(priceSlabArray);
      setPriceSlabErrors(validation.fieldErrors || {});
      if (!validation.isValid) {
        // Prevent form submission if validation fails
        // Errors are shown at field level, no need for top alert
        return;
      }
    }
    let _cess: Cess[] = [];
    let cessTotal = 0;
    taxConfiguration?.cess?.forEach((cessItem: any, index: any) => {
      if (!cessItem.cessName) return;
      const cessPercentage = data[cessItem.cessName];
      const cessAmount = (cessPercentage * quantity * unitPrice) / 100;
      cessTotal += cessAmount;
      _cess.push({
        cessName: cessItem.cessName,
        cessType: cessItem.cessType,
        cessPercentage,
        cessAmount: cessAmount?.toString(),
      });
    });
    const item = {
      materialName,
      hsnCode,
      quantity,
      unitPrice,
      amount: quantity * unitPrice,
      description,
      priceSlabArray,
      igst: 0,
      igstPercentage: 0,
      igstAmount: 0,
      cgst: 0,
      cgstPercentage: 0,
      cgstAmount: 0,
      sgst: 0,
      sgstPercentage: 0,
      sgstAmount: 0,
      lineTotal: 0,
      taxId: 0,
      taxName: "",
    };
    item.igst = 0;
    item.igstAmount = 0;
    item.igstPercentage = 0;
    item.cgst = 0;
    item.cgstAmount = 0;
    item.cgstPercentage = 0;
    item.sgst = 0;
    item.sgstAmount = 0;
    item.sgstPercentage = 0;
    // Helper function to extract tax rate from label (handles both "CGST 18%" and "CGST (18%)" formats)
    const extractTaxRateFromLabel = (label: string): number => {
      if (!label) return 0;
      // Try to extract rate from format like "CGST (18%)" or "CGST 18%"
      const match = label.match(/\(?(\d+\.?\d*)%\)?/);
      return match ? parseFloat(match[1]) : 0;
    };

    let selectedTax;
    switch (taxConfiguration?.taxType) {
      case "IGST":
        item.igst = igst;
        selectedTax = taxOptions.find(
          (dropdownItem) => dropdownItem.value === igst.toString()
        );
        if (selectedTax) {
          const igstRate =
            selectedTax.taxRate ||
            extractTaxRateFromLabel(selectedTax.label) ||
            0;
          item.igstPercentage = igstRate;
          item.igstAmount = (igstRate * item.amount) / 100;
          // Set taxId and taxName
          item.taxId = Number(selectedTax.value);
          item.taxName = selectedTax.taxName || "";
        } else {
          item.igstPercentage = 0;
          item.igstAmount = 0;
          item.taxId = 0;
          item.taxName = "";
        }
        break;
      case "CGST":
        item.cgst = cgst;
        item.sgst = cgst;
        selectedTax = taxOptions.find(
          (dropdownItem) => dropdownItem.value === cgst.toString()
        );

        if (selectedTax) {
          const cgstRate =
            selectedTax.taxRate ||
            extractTaxRateFromLabel(selectedTax.label) ||
            0;
          item.cgstPercentage = cgstRate / 2;
          item.sgstPercentage = cgstRate / 2;
          item.cgstAmount = (item.cgstPercentage * item.amount) / 100;
          item.sgstAmount = (item.sgstPercentage * item.amount) / 100;
          // Set taxId and taxName
          item.taxId = Number(selectedTax.value);
          item.taxName = selectedTax.taxName || "";
        } else {
          item.cgstPercentage = 0;
          item.sgstPercentage = 0;
          item.cgstAmount = 0;
          item.sgstAmount = 0;
          item.taxId = 0;
          item.taxName = "";
        }
        break;
      default:
        break;
    }

    if (igst) {
      item.igst = igst;
      const selectedTax = taxOptions.find(
        (dropdownItem: any) => dropdownItem.value === igst.toString()
      );
      if (selectedTax) {
        const igstRate =
          selectedTax.taxRate ||
          extractTaxRateFromLabel(selectedTax.label) ||
          0;
        item.igstPercentage = igstRate;
        item.igstAmount = (igstRate * item.amount) / 100;
        // Set taxId and taxName
        item.taxId = Number(selectedTax.value);
        item.taxName = selectedTax.taxName || "";
      } else {
        item.igstPercentage = 0;
        item.igstAmount = 0;
        item.taxId = 0;
        item.taxName = "";
      }
    }
    if (cgst) {
      item.cgst = cgst;
      item.sgst = cgst;
      const selectedTax = taxOptions.find(
        (dropdownItem: any) => dropdownItem.value === cgst.toString()
      );

      if (selectedTax) {
        const cgstRate =
          selectedTax.taxRate ||
          extractTaxRateFromLabel(selectedTax.label) ||
          0;
        item.cgstPercentage = cgstRate / 2;
        item.sgstPercentage = cgstRate / 2;
        item.cgstAmount = (item.cgstPercentage * item.amount) / 100;
        item.sgstAmount = (item.sgstPercentage * item.amount) / 100;
        // Set taxId and taxName
        item.taxId = Number(selectedTax.value);
        item.taxName = selectedTax.taxName || "";
      } else {
        item.cgstPercentage = 0;
        item.sgstPercentage = 0;
        item.cgstAmount = 0;
        item.sgstAmount = 0;
        item.taxId = 0;
        item.taxName = "";
      }
    }
    item.lineTotal =
      item.amount +
      item.igstAmount +
      item.cgstAmount +
      item.sgstAmount +
      cessTotal;
    const materialLabel = materialOptions.find(
      (item) => item.value == materialName
    )?.label;
    const materialCategoryLabel = materialCategoryOptions.find(
      (item: any) => item.value == materialCategory
    )?.label;
    const uomLabel = uomOptions.find((item) => item.value == uomId)?.label;
    if (formData.action == "add" || formData.index == null) {
      append({
        key: nanoid(),
        materialCategory,
        materialCategoryLabel,
        materialName,
        materialLabel,
        hsnCode,
        quantity,
        unitPrice,
        uomId,
        uomLabel,
        priceSlabArray,
        amount: item.amount.toString(),
        description,
        igst: item.igst.toString(),
        igstPercentage: item.igstPercentage.toString(),
        igstAmount: item.igstAmount.toString(),
        cgst: item.cgst.toString(),
        cgstPercentage: item.cgstPercentage.toString(),
        cgstAmount: item.cgstAmount.toString(),
        sgst: item.sgst.toString(),
        sgstPercentage: item.sgstPercentage.toString(),
        sgstAmount: item.sgstAmount.toString(),
        taxId: item.taxId,
        taxName: item.taxName,
        cess: _cess,
        lineTotal: item.lineTotal.toString(),
        needByDate,
        attachment: attachment?.length ? true : false,
        file: attachment,
        deliveryAddressId,
      });
    } else {
      update(formData.index, {
        ...formData.item,
        key: nanoid(),
        materialCategory,
        materialCategoryLabel,
        materialName,
        materialLabel,
        hsnCode,
        quantity,
        unitPrice,
        uomId,
        uomLabel,
        priceSlabArray,
        amount: item.amount.toString(),
        description,
        igst: item.igst.toString(),
        igstPercentage: item.igstPercentage.toString(),
        igstAmount: item.igstAmount.toString(),
        cgst: item.cgst.toString(),
        cgstPercentage: item.cgstPercentage.toString(),
        cgstAmount: item.cgstAmount.toString(),
        sgst: item.sgst.toString(),
        sgstPercentage: item.sgstPercentage.toString(),
        sgstAmount: item.sgstAmount.toString(),
        taxId: item.taxId,
        taxName: item.taxName,
        cess: _cess,
        lineTotal: item.lineTotal.toString(),
        needByDate,
        attachment: attachment?.length ? true : false,
        file: attachment,
        deliveryAddressId,
        dms: dmsFile ? formData?.item?.dms : [],
      });
    }
    // Clear any validation errors on successful submission
    setAlertInfo({
      type: "",
      message: "",
      errors: [],
    });
    closeModal();
  };
  const ShowAddressPopOver = ({ id, text, options, matchKey }: any) => {
    if (!id) {
      return <></>;
    }
    let address = options?.find((item: any) => item[matchKey] == id);
    if (!address) {
      return <></>;
    }
    return (
      <Popover
        content={
          <ul>
            <li>
              {address?.addressLine1} {address?.addressLine2}
            </li>
            <li>
              {address?.city}, {address?.stateName}, {address?.countryName} -{" "}
              {address?.pincode}
            </li>
          </ul>
        }
        title={address?.deliveryLocationName}
      >
        <a>{text}</a>
      </Popover>
    );
  };
  useEffect(() => {
    fetchDeliveryAddress();
  }, []);

  return (
    <Modal isOpen={modal} toggle={closeModal} size="xl">
      <ModalBody>
        <h5 className="title">
          {formData.action == "add" || formData.index == null
            ? "Add New Line"
            : "Update Line"}
        </h5>
        <div style={{ marginTop: "15px" }}></div>
        {alertInfo.message && (
          <div className="mb-3 mt-1">
            <Alert
              color={alertInfo.type === "error" ? "danger" : "success"}
              className="alert-icon"
            >
              <strong>{alertInfo.message}</strong>
              <Icon
                name={
                  alertInfo.type === "error" ? "alert-circle" : "check-circle"
                }
              />
              {alertInfo.errors.length > 0 ? (
                <ul>
                  {alertInfo.errors.map((err: any, index: any) => (
                    <li key={index}>{err}</li>
                  ))}
                </ul>
              ) : (
                ""
              )}
            </Alert>
          </div>
        )}
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(submitForm)}>
            <Row className="mt-12">
              <div className="col-md-4">
                <FormSelect
                  name="materialCategory"
                  label="Material Category"
                  required={true}
                  options={materialCategoryOptions}
                  placeholder="Select Material Category"
                  onChange={(e: any) => {
                    fetchMaterials(e, null);
                  }}
                />
              </div>
              <div className="col-md-4">
                <FormSelect
                  name="materialName"
                  label="Material Name"
                  required={true}
                  options={materialOptions.filter(
                    (option) => !selectedMaterials.includes(option.value)
                  )}
                  placeholder="Select Material Name"
                  onChange={(e: any) => {
                    fetchUom(e, materialData, null);
                  }}
                />
              </div>
              <div className="col-md-4">
                <FormInput
                  type="number"
                  // required={true}
                  name="hsnCode"
                  label="HSN/SAC"
                  pattern={/^$|^[0-9]{4,8}$/}
                  placeholder="Enter HSN"
                />
              </div>
            </Row>
            <div style={{ marginTop: "15px" }}></div>
            <Row>
              <div className="col-md-4">
                <FormSelect
                  name="uomId"
                  label="UOM"
                  required={true}
                  options={uomOptions}
                  placeholder="Units"
                />
              </div>
              {priceSlab == "false" && (
                <div className="col-md-4">
                  <FormInput
                    type="number"
                    pattern={/^\d+(\.\d+)?$/}
                    required={true}
                    min={1}
                    name="unitPrice"
                    label="Price per Unit"
                    placeholder="Enter Price per Unit"
                  />
                </div>
              )}
              {taxConfiguration?.gstType == "IGST" ? (
                <>
                  <div className="col-md-4">
                    <FormSelect
                      name="igst"
                      label="IGST(%)"
                      required={true}
                      options={getFilteredTaxOptions()}
                      placeholder="Enter IGST"
                    />
                  </div>
                </>
              ) : (
                <>
                  <div className="col-md-4">
                    <FormSelect
                      name="cgst"
                      label="Tax(%)"
                      required={true}
                      options={getFilteredTaxOptions()}
                      placeholder="Enter CGST"
                    />
                  </div>
                  <div style={{ marginTop: "15px" }}></div>
                </>
              )}
              {taxConfiguration?.cess?.map((item: any, index: any) => {
                if (!item.cessName) return null;
                return (
                  <>
                    {index === 0 && taxConfiguration?.gstType === "IGST" && (
                      <div style={{ marginTop: "15px" }}></div>
                    )}
                    <div className="col-md-4">
                      <FormInput
                        type="number"
                        required={true}
                        name={item.cessName}
                        label={item.cessName + "(%)"}
                        placeholder={`Enter ${item.cessName}`}
                      />
                    </div>
                  </>
                );
              })}
            </Row>
            <div style={{ marginTop: "15px" }}></div>
            <Row>
              <div className="col-md-4">
                {((files && files[0]?.name) ||
                  dmsFile ||
                  dmsFile?.fileName) && (
                  <div className="form-group m-0">
                    <div className="form-label-wrapper">
                      <label className="form-label" htmlFor={"attachment"}>
                        Attachment (If any)
                      </label>
                      <TooltipComponent
                        iconClass="card-hint"
                        icon="help-fill"
                        direction="left"
                        id={`file-tooltip`}
                        text="e.g: Design specificaitons"
                        containerClassName={""}
                      />
                    </div>
                    <div
                      className="form-control-wrap d-flex align-items-center justify-content-between border p-1"
                      style={{ borderRadius: "3px" }}
                    >
                      <span>
                        {(files && files[0]?.name) || dmsFile?.fileName}
                      </span>
                      <span
                        onClick={() => {
                          setValue("attachment", null);
                          setDmsFile(null);
                        }}
                        style={{
                          cursor: "pointer",
                          marginLeft: "10px",
                          display: "block",
                          fontSize: 14,
                        }}
                      >
                        <Icon name="cross"></Icon> &nbsp;
                      </span>
                    </div>
                  </div>
                )}
                <div
                  style={{
                    display: `${
                      (files && files[0]?.name) || dmsFile?.fileName
                        ? "none"
                        : "block"
                    }`,
                  }}
                >
                  <FormInput
                    type="file"
                    name="attachment"
                    label="Attachment (If any) "
                    placeholder="Enter Attachment"
                    tooltipText="e.g: Design specificaitons"
                  />
                </div>
              </div>
            </Row>
            <Row className="mt-4">
              <div className="col-md-12">
                <FormTextArea
                  name="description"
                  label="Description (if any)"
                  placeholder="Enter Description"
                  minLength={5}
                  maxLength={500}
                />
              </div>
            </Row>
            {priceSlab === "true" && (
              <>
                <div style={{ marginTop: "15px" }}></div>
                {priceSlabFields.map((item, index) => (
                  <Row key={item.id} align="middle">
                    {contractType !== "Open" && (
                      <div className="col-md-3">
                        <FormInput
                          type="number"
                          min={0.01}
                          required={true}
                          name={`priceSlabArray[${index}].minquantity`}
                          label="Minimum Qty"
                          placeholder="Enter Minimum Quantity"
                          pattern={/^\d+(\.\d{1,2})?$/}
                          patternErrorMessage="Quantity can have up to 2 decimal places"
                          onBlur={() => {
                            // Trigger validation when field loses focus
                            triggerValidation();
                          }}
                        />
                        {priceSlabErrors[index]?.minquantity && (
                          <div
                            className="text-danger"
                            style={{ fontSize: "12px", marginTop: "4px" }}
                          >
                            {priceSlabErrors[index].minquantity}
                          </div>
                        )}
                      </div>
                    )}
                    {contractType !== "Open" && (
                      <div className="col-md-3">
                        <FormInput
                          type="number"
                          min={0.01}
                          required={true}
                          name={`priceSlabArray[${index}].maxquantity`}
                          label="Maximum Qty"
                          placeholder="Enter Maximum Quantity"
                          pattern={/^\d+(\.\d{1,2})?$/}
                          patternErrorMessage="Quantity can have up to 2 decimal places"
                          onBlur={() => {
                            // Trigger validation when field loses focus
                            triggerValidation();
                          }}
                        />
                        {priceSlabErrors[index]?.maxquantity && (
                          <div
                            className="text-danger"
                            style={{ fontSize: "12px", marginTop: "4px" }}
                          >
                            {priceSlabErrors[index].maxquantity}
                          </div>
                        )}
                      </div>
                    )}
                    <div className={contractType === "Open" ? "col-md-6" : "col-md-3"}>
                      <FormInput
                        type="number"
                        pattern={/^\d+(\.\d+)?$/}
                        required={true}
                        min={1}
                        name={`priceSlabArray[${index}].unitPrice`}
                        label="Price per Unit"
                        placeholder="Enter Price per Unit"
                      />
                    </div>
                    <div className="col-md-3 d-flex align-items-center mt-4">
                      {contractType === "Open" ? null : index === 0 ? (
                        <Button
                          type="button"
                          color="primary"
                          onClick={() =>
                            appendPriceSlab({
                              minquantity: "",
                              maxquantity: "",
                              unitPrice: "",
                            })
                          }
                          className="btn-icon"
                        >
                          <Icon name="plus" />
                        </Button>
                      ) : (
                        <Button
                          type="button"
                          color="danger"
                          onClick={() => removePriceSlab(index)}
                          className="btn-icon"
                        >
                          <Icon name="minus" />
                        </Button>
                      )}
                    </div>
                    <div style={{ marginTop: "15px" }}></div>
                  </Row>
                ))}
              </>
            )}

            <Row className="mt-4">
              <Col lg="12">
                <ul className="align-center flex-wrap flex-sm-nowrap gx-4 gy-2">
                  {formData.action === "edit" && formData.index != null && (
                    <li>
                      <Button
                        className="btn btn-primary btn-md"
                        type="submit"
                        color="primary"
                      >
                        Update
                      </Button>
                    </li>
                  )}
                  {(formData.action == "add" || formData.index == null) && (
                    <li>
                      <Button
                        className="btn btn-primary btn-md"
                        type="submit"
                        color="primary"
                      >
                        Save
                      </Button>
                    </li>
                  )}
                  <li>
                    <a
                      href="#cancel"
                      onClick={(ev) => {
                        ev.preventDefault();
                        closeModal();
                      }}
                      className="link link-light"
                    >
                      Cancel
                    </a>
                  </li>
                </ul>
              </Col>
            </Row>
          </form>
        </FormProvider>
      </ModalBody>
    </Modal>
  );
};

export default MaterialModal;
